import isPropValid from "@emotion/is-prop-valid";
import { TabsProvider } from "features/CenterContent/shared/Tabs/useTabsContext";
import { TrainMapOverlayProvider } from "features/CenterContent/shared/TrainMapOverlayProvider";
import { ReactElement, ReactNode } from "react";
import { SporProvider, useColorMode } from "shared/components/Spor";
import { darkTheme } from "shared/theme/darkTheme";
import { lightTheme } from "shared/theme/lightTheme";
import { system } from "shared/theme/theme";
import { StyleSheetManager, ThemeProvider, WebTarget } from "styled-components";
// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, target: WebTarget) {
  if (typeof target === "string") {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}

type AppProps = {
  children: ReactNode;
};

/**
 * Wraps the app with the contexts required for full functionality
 */
export const AppProviders = ({ children }: AppProps): ReactElement | null => (
  <TrainMapOverlayProvider>
    <TabsProvider>{children}</TabsProvider>
  </TrainMapOverlayProvider>
);

const ThemeProviders = ({ children }: AppProps): ReactElement | null => {
  const { colorMode } = useColorMode();
  return (
    // If error occurs, check if lightTheme and darkTheme have exactly the same keys
    <ThemeProvider theme={colorMode === "dark" ? darkTheme : lightTheme}>
      {children}
    </ThemeProvider>
  );
};

/**
 * Wraps the app with style related contexts and global style components. Like SporProvider, ColorModeProvider and ThemeProvider
 */
export const StyleProviders = ({ children }: AppProps): ReactElement | null => (
  <StyleSheetManager shouldForwardProp={shouldForwardProp}>
    <SporProvider theme={system}>
      <ThemeProviders>{children}</ThemeProviders>
    </SporProvider>
  </StyleSheetManager>
);
